'use client';

import { PropsWithClassname } from '@/app/types';
import classNames from 'classnames';
import { ReactNode } from 'react';

import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import CrossIcon from '@/app/components/CrossIcon/CrossIcon';
import { DialogBackdrop, DialogPanel, Dialog as HeadlessUIDialog } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useIsIosOs } from '@/app/providers/ConfigProvider';

interface DialogProps extends PropsWithClassname {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  isCloseButtonVisible?: boolean;
  closeButtonText?: string;
}

const Dialog = ({
  isOpen,
  onClose,
  children,
  title,
  isCloseButtonVisible,
  closeButtonText,
  className,
}: DialogProps) => {
  const { t } = useTranslation();
  const isIosOs = useIsIosOs();

  return (
    <HeadlessUIDialog
      as="div"
      className={classNames(className, 'relative z-[200] opacity-1 transition duration-300 ease-out')}
      open={isOpen}
      onClose={onClose}
      transition
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/40 backdrop-blur-sm data-[closed]:opacity-0 transition duration-300 ease-out"
      />
      <div className="fixed inset-0 flex w-screen items-center justify-center">
        <DialogPanel
          transition
          className="relative flex flex-col w-full lg:min-w-[500px] lg:max-w-[600px]  h-full lg:h-auto max-h-[100vh] lg:max-h-[90vh] bg-white shadow-lg px-[16px] py-[16px] md:py-[24px] lg:py-[24px] lg:px-[32px] rounded-[4px] transition duration-300 ease-out data-[closed]:opacity-0 data-[closed]:translate-y-[-20px]"
        >
          <div className="relative">
            {!!title && <h3 className="m-0 text-[32px] font-medium leading-[40px] text-balance">{title}</h3>}
            <button
              className={classNames(
                'absolute p-[8px] rounded-full bg-white right-0 top-0 shadow-white',
                isIosOs ? 'active:text-primary-mid' : 'hover:text-primary-mid',
              )}
              onClick={onClose}
            >
              <CrossIcon />
            </button>
          </div>
          <div
            className={`pt-[16px] px-[8px] md:px-0 max-h-[calc(90vh-${isCloseButtonVisible ? 164 : 70}px)] grow-0 overflow-y-auto`}
          >
            {children}
          </div>

          {isCloseButtonVisible && (
            <div className="pt-[24px]">
              <Button
                tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onClose }}
                style={ButtonStyle.PRIMARY_OUTLINED}
              >
                {closeButtonText ?? t('components.Dialog.close')}
              </Button>
            </div>
          )}
        </DialogPanel>
      </div>
    </HeadlessUIDialog>
  );
};

export default Dialog;
